import { useEffect, useState, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { io } from 'socket.io-client'
import { tournamentsAPI } from '@/lib/api'
import BracketTreeView from '@/components/BracketTreeView'
import { Trophy, Users } from 'lucide-react'

/*
  Tournament beamer view — grote-scherm output voor de LAN.
  Views: lobby | bracket | spotlight | live | winner
  Wordt aangestuurd via /api/tournaments/:id/stage-state (control page).
  Zelf-contained styling — geen Layout wrapper.
*/
export default function TournamentStagePage() {
  const { id } = useParams()
  const [search] = useSearchParams()
  // ?overlay=1  →  transparente achtergrond voor OBS chroma-key
  // ?bg=<hex>   →  vaste achtergrond kleur (bijv. bg=00FF00 voor green-screen)
  const overlay = search.get('overlay') === '1'
  const bgOverride = search.get('bg')
  const [data, setData] = useState({ tournament: null, participants: [], matches: [], state: null })
  const [now, setNow] = useState(new Date())

  const load = async () => {
    try {
      const [tr, sr] = await Promise.all([
        tournamentsAPI.getTournament(id),
        tournamentsAPI.getStageState(id),
      ])
      setData({
        tournament:   tr.data.tournament,
        participants: tr.data.participants || [],
        matches:      tr.data.matches || [],
        state:        sr.data.state,
      })
    } catch {}
  }
  useEffect(() => { load() }, [id])

  // Tikkende klok (30s update)
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(t)
  }, [])

  // Realtime updates
  useEffect(() => {
    const token = localStorage.getItem('auth_token')
    const s = io({
      auth: token ? { token } : undefined,
      transports: ['websocket', 'polling'],
      reconnection: true,
    })
    s.on('tournament:stage-updated',  (p) => { if (Number(p.tournament_id) === Number(id)) load() })
    s.on('tournament:updated',        (p) => { if (Number(p.id)            === Number(id)) load() })
    s.on('tournament-started',        load)
    s.on('match-updated',             load)
    return () => { s.removeAllListeners(); s.disconnect() }
  }, [id])

  const bg = overlay
    ? 'transparent'
    : bgOverride
      ? `#${bgOverride}`
      : 'linear-gradient(180deg, #0E0E10 0%, #16161B 100%)'

  if (!data.tournament) {
    return <div style={{ background: bg, color: '#EDEDF0', minHeight: '100vh' }}
                className="flex items-center justify-center text-2xl font-mono">Laden…</div>
  }

  const view = data.state?.current_view || 'lobby'
  const spotlightMatch = data.matches.find(m => m.id === data.state?.spotlight_match_id)

  return (
    <div style={{ background: bg, color: '#EDEDF0', minHeight: '100vh', overflow: 'hidden' }}
         className="font-sans">
      {view === 'lobby'     && <LobbyView tournament={data.tournament} participants={data.participants} now={now} />}
      {view === 'bracket'   && <BracketView tournament={data.tournament} participants={data.participants} matches={data.matches} />}
      {view === 'spotlight' && <SpotlightView match={spotlightMatch} tournament={data.tournament} />}
      {view === 'live'      && <LiveView match={spotlightMatch} tournament={data.tournament} />}
      {view === 'winner'    && <WinnerView tournament={data.tournament} participants={data.participants} matches={data.matches} />}
    </div>
  )
}

/* ============ LOBBY ============ */
function LobbyView({ tournament, participants, now }) {
  const start = tournament.start_time ? new Date(tournament.start_time) : null
  const diff = start ? Math.max(0, Math.floor((start - now) / 1000)) : 0
  const hh = String(Math.floor(diff / 3600)).padStart(2, '0')
  const mm = String(Math.floor((diff % 3600) / 60)).padStart(2, '0')
  return (
    <div className="min-h-screen flex flex-col p-16">
      <div className="text-center mb-10">
        <div className="font-mono text-sm tracking-widest uppercase text-acid mb-4">Tournament · {tournament.game}</div>
        <h1 style={{ fontSize: 'clamp(64px, 8vw, 160px)', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1 }}>
          {tournament.name}
        </h1>
      </div>
      {start && diff > 0 && (
        <div className="text-center mb-12">
          <div className="font-mono text-xs uppercase tracking-widest text-muted mb-2" style={{ color: '#7A7B85' }}>Start over</div>
          <div className="font-mono text-acid" style={{ fontSize: 'clamp(80px, 12vw, 220px)', fontWeight: 700, letterSpacing: '.05em' }}>
            {hh}<span style={{ opacity: .3 }}>:</span>{mm}
          </div>
        </div>
      )}
      <div className="flex-1 grid grid-cols-4 gap-4 items-start content-start max-w-6xl mx-auto w-full">
        {participants.map(p => (
          <div key={p.id} className="rounded border p-3" style={{ background: '#1E1E24', borderColor: 'rgba(237,237,240,.14)' }}>
            <div className="font-semibold truncate">{p.team_name}</div>
            {p.checked_in_at && <div className="text-xs" style={{ color: '#61E294' }}>✓ ingecheckt</div>}
            {p.no_show      && <div className="text-xs" style={{ color: '#FF5A5A' }}>no-show</div>}
          </div>
        ))}
      </div>
      <div className="text-center font-mono text-xs uppercase tracking-widest text-muted mt-8" style={{ color: '#7A7B85' }}>
        <Users className="inline w-4 h-4 mr-2" />{participants.length} deelnemers · {participants.filter(p => p.checked_in_at).length} ingecheckt
      </div>
    </div>
  )
}

/* ============ BRACKET ============ */
function BracketView({ tournament, participants, matches }) {
  return (
    <div className="min-h-screen p-8">
      <div className="text-center mb-6">
        <div className="font-mono text-xs tracking-widest uppercase text-acid mb-2">Bracket · {tournament.game}</div>
        <h1 style={{ fontSize: 'clamp(32px, 4vw, 64px)', fontWeight: 700, letterSpacing: '-.01em' }}>{tournament.name}</h1>
      </div>
      <div className="max-w-[95vw] overflow-auto mx-auto">
        <BracketTreeView tournament={tournament} participants={participants} matches={matches} isAdmin={false} />
      </div>
    </div>
  )
}

/* ============ SPOTLIGHT (match preview) ============ */
function SpotlightView({ match, tournament }) {
  if (!match) return <div className="min-h-screen flex items-center justify-center text-3xl font-mono" style={{ color: '#7A7B85' }}>Geen match geselecteerd</div>
  return (
    <div className="min-h-screen flex flex-col p-16">
      <div className="text-center mb-8">
        <div className="font-mono text-sm tracking-widest uppercase text-acid mb-2">Volgende match · {tournament.game}</div>
        <div className="font-mono text-xs uppercase tracking-widest" style={{ color: '#7A7B85' }}>Round {match.round} · Match {match.match_number}</div>
      </div>
      <div className="flex-1 grid grid-cols-[1fr_auto_1fr] gap-12 items-center">
        <TeamSlot name={match.participant1_name} logo={match.participant1_logo} align="right" />
        <div className="text-center" style={{ fontSize: 'clamp(80px, 10vw, 200px)', fontWeight: 700, letterSpacing: '.05em', color: 'rgb(180,255,57)', animation: 'vsPop 800ms cubic-bezier(.34,1.56,.64,1) both' }}>vs</div>
        <TeamSlot name={match.participant2_name} logo={match.participant2_logo} align="left" />
      </div>
    </div>
  )
}
function TeamSlot({ name, logo, align }) {
  const slideFrom = align === 'right' ? '-40px' : '40px'
  const anim = { animation: `slideIn 700ms cubic-bezier(.2,.7,.3,1) both`, '--slide-x': slideFrom }
  return (
    <div className={`flex flex-col ${align === 'right' ? 'items-end text-right' : 'items-start text-left'}`}
         style={anim} key={name /* remount → re-animate bij match-wissel */}>
      {logo && <img src={logo} alt="" style={{ width: 200, height: 200, objectFit: 'contain', marginBottom: 16, borderRadius: 8 }} />}
      <div style={{ fontSize: 'clamp(40px, 5vw, 96px)', fontWeight: 700, letterSpacing: '-.01em', lineHeight: 1 }}>
        {name || <span style={{ color: '#7A7B85' }}>tba</span>}
      </div>
    </div>
  )
}

// Injecteer keyframe één keer (module-load)
if (typeof document !== 'undefined' && !document.getElementById('tstage-anim')) {
  const st = document.createElement('style'); st.id = 'tstage-anim'
  st.textContent = `\n@keyframes slideIn { from { opacity:0; transform: translateX(var(--slide-x, 40px)); } to { opacity:1; transform: translateX(0); } }\n@keyframes vsPop { 0% { opacity:0; transform: scale(.4); } 60% { opacity:1; transform: scale(1.15); } 100% { transform: scale(1); } }\n`
  document.head.appendChild(st)
}

/* ============ LIVE (live scoreboard) ============ */
function LiveView({ match, tournament }) {
  if (!match) return <div className="min-h-screen flex items-center justify-center text-3xl font-mono" style={{ color: '#7A7B85' }}>Geen live match</div>
  if (tournament.type === 'deathmatch' && match.deathmatch_scores) {
    const sorted = [...match.deathmatch_scores].sort((a, b) => b.score - a.score)
    return (
      <div className="min-h-screen p-12">
        <div className="text-center mb-6">
          <div className="font-mono text-xs tracking-widest uppercase text-acid mb-2">Live · Deathmatch</div>
          <h1 style={{ fontSize: 'clamp(40px, 5vw, 80px)', fontWeight: 700 }}>{tournament.name}</h1>
        </div>
        <div className="max-w-3xl mx-auto space-y-3">
          {sorted.map((s, i) => (
            <div key={s.participant_id} className="flex items-center gap-4 p-4 rounded"
                 style={{ background: i === 0 ? 'rgba(180,255,57,.08)' : '#1E1E24', border: i === 0 ? '2px solid rgb(180,255,57)' : '1px solid rgba(237,237,240,.14)' }}>
              <div className="font-mono text-2xl w-10 tabular-nums" style={{ color: i === 0 ? 'rgb(180,255,57)' : '#7A7B85' }}>{i + 1}</div>
              <div className="flex-1 text-2xl font-semibold">{s.team_name}</div>
              <div className="text-4xl font-bold tabular-nums" style={{ color: i === 0 ? 'rgb(180,255,57)' : '#EDEDF0' }}>{s.score}</div>
            </div>
          ))}
        </div>
      </div>
    )
  }
  // Regular 1v1: toon groot scoreboard
  return (
    <div className="min-h-screen flex flex-col p-16">
      <div className="text-center mb-6">
        <div className="font-mono text-xs tracking-widest uppercase text-acid">Live match</div>
        <div className="font-mono text-xs uppercase tracking-widest" style={{ color: '#7A7B85' }}>Round {match.round} · Match {match.match_number}</div>
      </div>
      <div className="flex-1 grid grid-cols-3 gap-8 items-center">
        <div className="text-center">
          <div style={{ fontSize: 'clamp(32px, 4vw, 72px)', fontWeight: 700 }}>{match.participant1_name}</div>
          <div style={{ fontSize: 'clamp(120px, 18vw, 300px)', fontWeight: 900, color: 'rgb(180,255,57)', lineHeight: 1 }} className="font-mono tabular-nums">
            {match.participant1_score ?? 0}
          </div>
        </div>
        <div className="text-center font-mono" style={{ fontSize: 'clamp(48px, 6vw, 128px)', color: '#7A7B85' }}>·</div>
        <div className="text-center">
          <div style={{ fontSize: 'clamp(32px, 4vw, 72px)', fontWeight: 700 }}>{match.participant2_name}</div>
          <div style={{ fontSize: 'clamp(120px, 18vw, 300px)', fontWeight: 900, color: 'rgb(180,255,57)', lineHeight: 1 }} className="font-mono tabular-nums">
            {match.participant2_score ?? 0}
          </div>
        </div>
      </div>
    </div>
  )
}

/* ============ WINNER ============ */
function WinnerView({ tournament, participants }) {
  const winner = participants.find(p => p.placement === 1)
  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-16 relative">
      <ConfettiOverlay />
      <div className="font-mono text-sm tracking-widest uppercase text-acid mb-4">Winnaar · {tournament.name}</div>
      <Trophy className="w-32 h-32 mb-8" style={{ color: 'rgb(228,194,101)' }} />
      <h1 style={{ fontSize: 'clamp(72px, 10vw, 200px)', fontWeight: 800, letterSpacing: '-.02em', lineHeight: 1, color: 'rgb(228,194,101)' }}>
        {winner?.team_name || '—'}
      </h1>
    </div>
  )
}

function ConfettiOverlay() {
  const ref = useRef(null)
  useEffect(() => {
    const canvas = ref.current; if (!canvas) return
    const ctx = canvas.getContext('2d')
    const dpr = Math.min(2, window.devicePixelRatio || 1)
    const fit = () => {
      const r = canvas.getBoundingClientRect()
      canvas.width = Math.max(2, Math.floor(r.width * dpr))
      canvas.height = Math.max(2, Math.floor(r.height * dpr))
    }
    fit()
    const ro = new ResizeObserver(fit); ro.observe(canvas)
    const COLORS = ['#B4FF39', '#FFB57A', '#E4C265', '#FF5A5A', '#61E294', '#ffffff']
    const parts = Array.from({ length: 160 }, () => ({
      x: Math.random() * canvas.width,
      y: -Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 60 * dpr,
      vy: (60 + Math.random() * 120) * dpr,
      size: (3 + Math.random() * 4) * dpr,
      rot: Math.random() * Math.PI, vrot: (Math.random() - 0.5) * 6,
      color: COLORS[(Math.random() * COLORS.length) | 0],
    }))
    let last = performance.now(), raf
    const loop = (now) => {
      const dt = Math.min(.05, (now - last) / 1000); last = now
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      for (const p of parts) {
        p.x += p.vx * dt; p.y += p.vy * dt; p.rot += p.vrot * dt
        if (p.y > canvas.height + 20) { p.y = -20; p.x = Math.random() * canvas.width }
        ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.rot)
        ctx.fillStyle = p.color
        ctx.fillRect(-p.size, -p.size / 2, p.size * 2, p.size)
        ctx.restore()
      }
      raf = requestAnimationFrame(loop)
    }
    raf = requestAnimationFrame(loop)
    return () => { cancelAnimationFrame(raf); ro.disconnect() }
  }, [])
  return <canvas ref={ref} className="fixed inset-0 pointer-events-none" style={{ zIndex: 50, width: '100vw', height: '100vh' }} />
}
